import React from 'react';
import {
  Bell,
  Bot,
  Brain,
  Code,
  FileCode,
  FileSignature,
  FileText,
  Instagram,
  Languages,
  PhoneCall,
  Plug,
  Puzzle,
  Reply,
  Shield,
  Clock,
  Smartphone,
  Store,
  Tag,
  Users,
  Warehouse,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

export type SettingsGroupId = 'general' | 'team' | 'inbox' | 'channels' | 'ai' | 'account';
export type SettingsRole = 'admin' | 'agent';

export interface SettingsGroupDef {
  id: SettingsGroupId;
  labelKey: string;
  labelFallback: string;
}

export interface SettingsSectionDef {
  id: string;
  group: SettingsGroupId;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  icon: LucideIcon | React.ComponentType<{ className?: string }>;
  /** Wer den Eintrag sieht. Ohne Angabe: nur Admins. */
  roles?: SettingsRole[];
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
}

export const SETTINGS_GROUPS: SettingsGroupDef[] = [
  { id: 'general', labelKey: 'settings:group.general', labelFallback: 'Allgemein' },
  { id: 'team', labelKey: 'settings:group.team', labelFallback: 'Team' },
  { id: 'inbox', labelKey: 'settings:group.inbox', labelFallback: 'Posteingang' },
  { id: 'channels', labelKey: 'settings:group.channels', labelFallback: 'Kanäle' },
  { id: 'ai', labelKey: 'settings:group.ai', labelFallback: 'KI & Integrationen' },
  { id: 'account', labelKey: 'settings:group.account', labelFallback: 'Konto' },
];

/** Named exports müssen für React.lazy auf `default` gemappt werden. */
const named = <T extends Record<string, any>, K extends keyof T>(
  loader: () => Promise<T>,
  key: K,
) => React.lazy(() => loader().then((m) => ({ default: m[key] })));

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  /* ------------------------------------------------------------ Allgemein */
  {
    id: 'language',
    group: 'general',
    titleKey: 'settings:section.language.title',
    titleFallback: 'Sprache',
    descKey: 'settings:section.language.description',
    descFallback: 'Anzeigesprache der Oberfläche',
    icon: Languages,
    roles: ['admin', 'agent'],
    Component: React.lazy(() => import('@/components/settings/LanguageSection')),
  },
  {
    id: 'mobile-app',
    group: 'general',
    titleKey: 'settings:section.mobile-app.title',
    titleFallback: 'Mobile App',
    descKey: 'settings:section.mobile-app.description',
    descFallback: 'Mobilgerät per QR-Code mit diesem Desk verbinden',
    icon: Smartphone,
    roles: ['admin', 'agent'],
    Component: React.lazy(() => import('@/components/settings/MobileAppSection')),
  },
  {
    id: 'notification-recipients',
    group: 'general',
    titleKey: 'settings:section.notification-recipients.title',
    titleFallback: 'Benachrichtigungen',
    descKey: 'settings:section.notification-recipients.description',
    descFallback: 'E-Mail-Empfänger für Benachrichtigungen verwalten',
    icon: Bell,
    Component: React.lazy(() => import('@/components/EmailSubscriptionsCard')),
  },

  /* ----------------------------------------------------------------- Team */
  {
    id: 'users',
    group: 'team',
    titleKey: 'settings:section.users.title',
    titleFallback: 'Benutzerverwaltung',
    descKey: 'settings:section.users.description',
    descFallback: 'Benutzer, Rollen und Organisationszugriffe verwalten',
    icon: Users,
    Component: React.lazy(() => import('@/components/settings/sections/UsersSection')),
  },
  {
    id: 'organizations',
    group: 'team',
    titleKey: 'settings:section.organizations.title',
    titleFallback: 'Organisationen',
    descKey: 'settings:section.organizations.description',
    descFallback: 'Mandanten und E-Mail-Infrastruktur verwalten',
    icon: Store,
    Component: named(() => import('@/components/OrganizationsSection'), 'OrganizationsSection'),
  },
  {
    id: 'lager',
    group: 'team',
    titleKey: 'settings:section.lager.title',
    titleFallback: 'Lager',
    descKey: 'settings:section.lager.description',
    descFallback: 'Support-Empfänger für Lager-Mitarbeiter',
    icon: Warehouse,
    Component: React.lazy(() => import('@/components/WarehouseSettingsSection')),
  },
  {
    id: 'contacts',
    group: 'team',
    titleKey: 'settings:section.contacts.title',
    titleFallback: 'Häufige Kontakte',
    descKey: 'settings:section.contacts.description',
    descFallback: 'Kontaktverzeichnis für schnellen Zugriff',
    icon: Users,
    roles: ['admin', 'agent'],
    Component: React.lazy(() => import('@/components/FrequentContactsSection')),
  },

  /* ----------------------------------------------------------- Posteingang */
  {
    id: 'send',
    group: 'inbox',
    titleKey: 'settings:section.send.title',
    titleFallback: 'Senden',
    descKey: 'settings:section.send.description',
    descFallback: 'Senden rückgängig machen (Versand verzögern)',
    icon: Clock,
    roles: ['admin', 'agent'],
    Component: React.lazy(() => import('@/components/settings/SendSection')),
  },
  {
    id: 'snippets',
    group: 'inbox',
    titleKey: 'settings:section.snippets.title',
    titleFallback: 'Textbausteine',
    descKey: 'settings:section.snippets.description',
    descFallback: 'Wiederverwendbare Antworten und Shortcuts',
    icon: FileText,
    roles: ['admin', 'agent'],
    Component: React.lazy(() => import('@/components/settings/sections/SnippetsSection')),
  },
  {
    id: 'signatures',
    group: 'inbox',
    titleKey: 'settings:section.signatures.title',
    titleFallback: 'E-Mail-Signaturen',
    descKey: 'settings:section.signatures.description',
    descFallback: 'Persönliche Signaturen verwalten',
    icon: FileSignature,
    roles: ['admin', 'agent'],
    Component: named(() => import('@/components/SignatureSection'), 'SignatureSection'),
  },
  {
    id: 'auto-replies',
    group: 'inbox',
    titleKey: 'settings:section.auto-replies.title',
    titleFallback: 'Auto-Antworten',
    descKey: 'settings:section.auto-replies.description',
    descFallback: 'Automatische Eingangsbestätigungen pro Organisation',
    icon: Reply,
    Component: React.lazy(() => import('@/components/settings/sections/AutoRepliesSection')),
  },
  {
    id: 'tags',
    group: 'inbox',
    titleKey: 'settings:section.tags.title',
    titleFallback: 'Tags',
    descKey: 'settings:section.tags.description',
    descFallback: 'Ticket-Tags verwalten und organisieren',
    icon: Tag,
    roles: ['admin', 'agent'],
    Component: named(() => import('@/components/TagsTab'), 'TagsTab'),
  },
  {
    id: 'sla',
    group: 'inbox',
    titleKey: 'settings:section.sla.title',
    titleFallback: 'SLA',
    descKey: 'settings:section.sla.description',
    descFallback: 'Reaktionszeiten, Arbeitszeiten und Feiertage',
    icon: Clock,
    Component: named(() => import('@/components/SLASettings'), 'SLASettings'),
  },
  {
    id: 'form-setup',
    group: 'inbox',
    titleKey: 'settings:section.formSetup.title',
    titleFallback: 'Formular-Einrichtung',
    descKey: 'settings:section.formSetup.description',
    descFallback: 'Website-Formulare als Ticket im Namen des Kunden',
    icon: FileCode,
    Component: React.lazy(() => import('@/components/settings/FormSetupSection')),
  },

  /* --------------------------------------------------------------- Kanäle */
  {
    id: 'voice',
    group: 'channels',
    titleKey: 'settings:section.voice.title',
    titleFallback: 'Telefonie',
    descKey: 'settings:section.voice.description',
    descFallback: 'Telnyx-Verbindung für Anrufe im Browser',
    icon: PhoneCall,
    Component: React.lazy(() => import('@/components/settings/VoiceSection')),
  },
  {
    id: 'whatsapp',
    group: 'channels',
    titleKey: 'settings:section.whatsapp.title',
    titleFallback: 'WhatsApp',
    descKey: 'settings:section.whatsapp.description',
    descFallback: 'WhatsApp Business — Nachrichten werden zu Tickets',
    icon: WhatsAppIcon,
    Component: React.lazy(() => import('@/components/settings/WhatsappSection')),
  },
  {
    id: 'meta-messaging',
    group: 'channels',
    titleKey: 'settings:section.metaMessaging.title',
    titleFallback: 'Messenger & Instagram',
    descKey: 'settings:section.metaMessaging.description',
    descFallback: 'Facebook- und Instagram-Nachrichten werden zu Tickets',
    icon: Instagram,
    Component: React.lazy(() => import('@/components/settings/MetaMessagingSection')),
  },
  {
    id: 'chatbot',
    group: 'channels',
    titleKey: 'settings:section.chatbot.title',
    titleFallback: 'Chatbot',
    descKey: 'settings:section.chatbot.description',
    descFallback: 'Website-Chatbot Widgets verwalten',
    icon: Bot,
    Component: React.lazy(() => import('@/components/ChatbotSection')),
  },

  /* ----------------------------------------------- KI & Integrationen */
  {
    id: 'ai-training',
    group: 'ai',
    titleKey: 'settings:section.ai-training.title',
    titleFallback: 'KI-Training',
    descKey: 'settings:section.ai-training.description',
    descFallback: 'Die KI mit eigenen Anweisungen trainieren',
    icon: Brain,
    Component: React.lazy(() => import('@/components/AITrainingManagement')),
  },
  {
    id: 'integrations',
    group: 'ai',
    titleKey: 'settings:section.integrations.title',
    titleFallback: 'Shop & Zahlungen',
    descKey: 'settings:section.integrations.description',
    descFallback: 'Shopify, JTL, WooCommerce, Shopware, Magento, BigCommerce, PrestaShop, İkas',
    icon: Zap,
    Component: React.lazy(() => import('@/components/settings/sections/IntegrationsSection')),
  },
  {
    id: 'plugins',
    group: 'ai',
    titleKey: 'settings:section.plugins.title',
    titleFallback: 'Plugins',
    descKey: 'settings:section.plugins.description',
    descFallback: 'PowerfulForm und andere Erweiterungen',
    icon: Puzzle,
    Component: React.lazy(() => import('@/components/PluginsTab')),
  },
  {
    id: 'mcp',
    group: 'ai',
    titleKey: 'settings:section.mcp.title',
    titleFallback: 'MCP-Zugriff',
    descKey: 'settings:section.mcp.description',
    descFallback: 'KI-Clients mit VynDesk verbinden',
    icon: Plug,
    Component: React.lazy(() => import('@/components/settings/McpSection')),
  },
  {
    id: 'developer',
    group: 'ai',
    titleKey: 'settings:section.developer.title',
    titleFallback: 'Developer',
    descKey: 'settings:section.developer.description',
    descFallback: 'API-Keys und externe Integrationen',
    icon: Code,
    Component: named(() => import('@/components/DeveloperSection'), 'DeveloperSection'),
  },

  /* ---------------------------------------------------------------- Konto */
  {
    id: 'license',
    group: 'account',
    titleKey: 'settings:section.license.title',
    titleFallback: 'Lizenz',
    descKey: 'settings:section.license.description',
    descFallback: 'Plan, Limits und Abrechnung',
    icon: Shield,
    Component: React.lazy(() => import('@/components/LicenseSection')),
  },
];

/** Alte Pfade/Tabs, die weiterhin erreichbar bleiben müssen. */
export const LEGACY_SECTION_ALIASES: Record<string, string> = {
  roles: 'users',
  shops: 'integrations',
  shopify: 'integrations',
  jtl: 'integrations',
  email: 'organizations',
  tickets: 'sla',
  notifications: 'notification-recipients',
  general: 'language',
  metaMessaging: 'meta-messaging',
  formSetup: 'form-setup',
  mobileApp: 'mobile-app',
  autoReplies: 'auto-replies',
  aiTraining: 'ai-training',
};

export function canSeeSection(section: SettingsSectionDef, role: string | undefined): boolean {
  if (role === 'admin') return true;
  if (role !== 'agent') return false;
  return (section.roles ?? ['admin']).includes('agent');
}

export function visibleSections(role: string | undefined): SettingsSectionDef[] {
  return SETTINGS_SECTIONS.filter((section) => canSeeSection(section, role));
}

/** Einstiegspunkt ohne Abschnitt: Admins landen auf der Benutzerverwaltung. */
const DEFAULT_SECTION_ORDER = ['users', 'language'];

export function defaultSectionId(role: string | undefined): string | null {
  const sections = visibleSections(role);
  if (!sections.length) return null;
  for (const id of DEFAULT_SECTION_ORDER) {
    if (sections.some((section) => section.id === id)) return id;
  }
  return sections[0].id;
}

export function resolveSectionId(raw: string | undefined, role: string | undefined): string | null {
  const sections = visibleSections(role);
  if (!sections.length) return null;

  const candidate = raw ? (LEGACY_SECTION_ALIASES[raw] ?? raw) : null;
  const match = candidate ? sections.find((section) => section.id === candidate) : undefined;
  return match ? match.id : defaultSectionId(role);
}
